import * as dateFns from 'date-fns'
import * as React from 'react'
import {useLocation} from 'react-router-dom'

import {useAppDispatch, useAppSelector} from '../../../../../../hooks/react-redux'
import {useSetUserProperty} from '../../../../../../hooks/useSetUserProperty'
import {useUserProperty} from '../../../../../../hooks/useUserProperty'
import {isPipelinesEditScreen} from '../../../../../../routes'
import {UserProperties} from '../../../../../../utils/userProperties'

import {feedbackForm, initialState, ShowFeedbackFormReasons} from './Feedback.slice'

const useShouldShowAfterTimeout = () => {
  const today = React.useMemo(() => new Date(), [])
  const dispatch = useAppDispatch()

  const startedUsingPipelinesDateTime = useUserProperty(
    UserProperties.HAS_STARTED_USING_PIPELINES_DATETIME,
  )
  const setUserProperty = useSetUserProperty()

  const started = React.useMemo(() => {
    let result = dateFns.parseISO(startedUsingPipelinesDateTime ?? '')
    if (result instanceof Date && isNaN(result.getMilliseconds())) {
      result = today
      setUserProperty(UserProperties.HAS_STARTED_USING_PIPELINES_DATETIME, dateFns.formatISO(today))
    }

    return result
  }, [setUserProperty, startedUsingPipelinesDateTime, today])

  React.useEffect(() => {
    if (dateFns.differenceInWeeks(today, started) > 0) {
      dispatch(
        feedbackForm.actions.setVisibility({
          visible: true,
          reason: ShowFeedbackFormReasons.FOLLOW_UP,
        }),
      )
    }
  }, [dispatch, started, today])
}

/* eslint-disable @typescript-eslint/no-magic-numbers */
const MINUTE = 60 * 1000
const STUCK_TIMEOUT = 5 * MINUTE
/* eslint-enable @typescript-eslint/no-magic-numbers */

const useShouldShowIfStuck = () => {
  const dispatch = useAppDispatch()
  const timer = React.useRef<null | number>(null)
  const location = useLocation()
  const isEditingPipelines = isPipelinesEditScreen(location)

  React.useEffect(() => {
    if (isEditingPipelines) {
      timer.current = window.setTimeout(() => {
        dispatch(
          feedbackForm.actions.setVisibility({
            visible: true,
            reason: ShowFeedbackFormReasons.STUCK,
          }),
        )
      }, STUCK_TIMEOUT)
    }

    return () => {
      timer.current != null && window.clearTimeout(timer.current)
    }
  }, [dispatch, isEditingPipelines])
}

const notVisibleDefault = initialState.visibility
export const useShouldShowFeedbackForm = () => {
  useShouldShowAfterTimeout()
  useShouldShowIfStuck()

  const hasSubmittedPipelinesFeedback = useUserProperty(
    UserProperties.HAS_SUBMITTED_PIPELINES_FEEDBACK_FORM,
  )

  const shouldBeVisible = useAppSelector(state => state.pipelines.feedbackForm.visibility)

  return hasSubmittedPipelinesFeedback === 'true' ? notVisibleDefault : shouldBeVisible
}
